import React, { useState, useEffect } from 'react';
import { Box, Grid, CircularProgress, Alert, Table, TableHead, TableRow, TableCell, TableBody,
  Tooltip, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import { DetailRow, InvTextField } from '../../components/InvField';
import { getRequisition, getRequisitionItems, getRequisitionIssues, getIssueItems,
  submitRequisition, approveRequisition, rejectRequisition, deleteRequisition,
  releaseRequisitionReservation } from '../../api/dispensing';
import { getStores } from '../../api/stockLedger';
import useItemNames from '../../hooks/useItemNames';
import { formatDate } from '../../utils';
import { resolveDispensingScope, normalizeIssueRow, resolveIssueId, resolveIssueStatus,
  formatDispensingError, resolveLineId } from '../../utils/dispensingEntity';
import { REQUISITION_ISSUABLE_STATUSES } from '../../constants';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';

/** ConfirmDialog action keys on this screen → the permission each one needs. */
const DIALOG_ACTION_PERMISSION = {
  submit: INV_ACTIONS.REQUISITION_SUBMIT,
  approve: INV_ACTIONS.REQUISITION_APPROVE,
  reject: INV_ACTIONS.REQUISITION_REJECT,
  release: INV_ACTIONS.REQUISITION_RELEASE,
  cancel: INV_ACTIONS.REQUISITION_CANCEL,
};

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.data || data?.items || []);
}

function BadgeStack({ label, children }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: IPD_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

/** Most recent issue against this requisition — drives the Issue Status badge and timeline. */
function getLatestIssue(issues) {
  if (!issues?.length) return null;
  return [...issues].sort((a, b) => (Number(b.issue_sequence) || 0) - (Number(a.issue_sequence) || 0)
    || new Date(b.transaction_date || 0) - new Date(a.transaction_date || 0))[0];
}

const TIMELINE_STEPS = ['Create', 'Submit', 'Approve', 'Issue', 'Awaiting Acknowledgement', 'Acknowledged'];

/**
 * Maps the requisition + its latest issue onto a step index for the visual timeline.
 * Latest issue status wins once an issue exists (it's the more advanced signal);
 * otherwise falls back to the requisition's own status for the earlier steps.
 */
function resolveTimelineStep(reqStatus, latestIssueStatus) {
  if (reqStatus === 'REJECTED') return { index: 2, terminal: 'Rejected' };
  if (reqStatus === 'CANCELLED') return { index: 1, terminal: 'Cancelled' };
  if (latestIssueStatus === 'ACKNOWLEDGED' || latestIssueStatus === 'COMPLETED') return { index: 5 };
  if (latestIssueStatus === 'ISSUED') return { index: 4 };
  if (latestIssueStatus === 'DRAFT') return { index: 3 };
  if (['APPROVED', 'RESERVED', 'PARTIALLY_ISSUED', 'ISSUED'].includes(reqStatus)) return { index: 3 };
  if (reqStatus === 'SUBMITTED') return { index: 1 };
  return { index: 0 };
}

/** Compact status-driven step indicator — same circle/connector technique as VendorWorkflowBar. */
function RequisitionTimeline({ reqStatus, latestIssueStatus }) {
  const { index: currentIndex, terminal } = resolveTimelineStep(reqStatus, latestIssueStatus);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', overflowX: 'auto', pb: 0.5, mb: 2.5 }}>
      {TIMELINE_STEPS.map((label, i) => {
        const isTerminalHere = terminal && i === currentIndex;
        const isCompleted = !terminal && i < currentIndex;
        const isActive = !terminal && i === currentIndex;
        const isLast = i === TIMELINE_STEPS.length - 1;

        let circleBg = IPD_COLORS.cardBg;
        let circleBorder = IPD_COLORS.border;
        let circleColor = IPD_COLORS.primary;
        let stepLabel = label;

        if (isTerminalHere) {
          circleBg = IPD_COLORS.error.text;
          circleBorder = IPD_COLORS.error.text;
          circleColor = '#fff';
          stepLabel = terminal;
        } else if (isCompleted || isActive) {
          circleBg = IPD_COLORS.primary;
          circleBorder = IPD_COLORS.primary;
          circleColor = '#fff';
        }

        return (
          <Box key={label} sx={{ display: 'flex', alignItems: 'flex-start', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: { xs: 76, sm: 96 } }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center',
                justifyContent: 'center', border: `2px solid ${circleBorder}`, background: circleBg,
                color: circleColor, fontWeight: 700, fontSize: 12, flexShrink: 0,
              }}>
                {isCompleted ? <CheckIcon sx={{ fontSize: 16 }} /> : i + 1}
              </Box>
              <Typography sx={{
                mt: 1, fontSize: 10.5, fontWeight: isActive || isTerminalHere ? 700 : 600,
                color: isTerminalHere ? IPD_COLORS.error.text : isActive ? IPD_COLORS.primaryDark : IPD_COLORS.textSecondary,
                textAlign: 'center', lineHeight: 1.25, px: 0.5,
              }}>
                {stepLabel}
              </Typography>
            </Box>
            {!isLast && (
              <Box sx={{
                width: { xs: 20, sm: 28 }, height: 2, mt: '15px', flexShrink: 0,
                background: isCompleted ? IPD_COLORS.primary : IPD_COLORS.border, borderRadius: 1,
              }} />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function storeLabel(store) {
  if (!store) return null;
  const name = store.store_name || '';
  const code = store.store_code || '';
  return code ? `${name} (${code})` : name || null;
}

/** Format qty for display; blank/null → em dash. */
function formatQty(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return String(value);
}

/**
 * Build approve payload items from editable qtys.
 * Returns { error } or { items } where items is null for full approve (omit from body).
 */
function buildApproveItems(reqItems, approvedQtys) {
  if (!reqItems.length) return { error: 'No items to approve.' };
  const items = [];
  let anyPartial = false;
  let totalApproved = 0;

  for (const item of reqItems) {
    const lineId = resolveLineId(item);
    if (!lineId) {
      return { error: 'One or more lines are missing an ID. Reload and try again.' };
    }
    const requested = Number(item.requested_quantity) || 0;
    const raw = approvedQtys[lineId];
    const approved = raw === '' || raw == null
      ? requested
      : Number(raw);
    if (Number.isNaN(approved) || approved < 0) {
      return { error: 'Approved quantity must be zero or a positive number.' };
    }
    if (approved > requested) {
      return { error: 'Approved quantity cannot exceed requested quantity.' };
    }
    if (approved !== requested) anyPartial = true;
    totalApproved += approved;
    items.push({ id: lineId, approved_quantity: String(approved) });
  }

  if (totalApproved <= 0) {
    return { error: 'Approve at least one line with quantity greater than zero.' };
  }
  return { items: anyPartial ? items : null };
}

export default function RequisitionDetail() {
  const { id } = useParams();
  const { url } = useRouteMatch();
  const history = useHistory();
  const { can } = useInvRole();
  const { resolveItem } = useItemNames();
  const [req, setReq] = useState(null);
  const [reqItems, setReqItems] = useState([]);
  const [issues, setIssues] = useState([]);
  const [issuedQtyByIssue, setIssuedQtyByIssue] = useState({});
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState('');
  const [actioning, setActioning] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [approvedQtys, setApprovedQtys] = useState({});

  const stockBase = url.split('/requisitions/')[0];

  async function load() {
    setLoading(true); setError(null);
    try {
      const [r, items, issueList, storesData] = await Promise.all([
        getRequisition(id),
        getRequisitionItems(id).catch(e => { console.error('[RequisitionDetail] load items failed:', e); return []; }),
        getRequisitionIssues(id).catch(e => { console.error('[RequisitionDetail] load issues failed:', e); return []; }),
        getStores().catch(e => { console.error('[RequisitionDetail] load stores failed:', e); return []; }),
      ]);
      const list = normalizeList(items);
      const issueRows = normalizeList(issueList).map(normalizeIssueRow);
      setReq(r);
      setReqItems(list);
      setIssues(issueRows);
      setStores(normalizeList(storesData));

      // Per-issue Issued Qty isn't on the issue list row — sum it from each issue's items.
      // Small N (typically 1-3 issues per requisition), fetched in parallel.
      const qtyEntries = await Promise.all(issueRows.map(async (row) => {
        const issueId = resolveIssueId(row);
        if (!issueId) return null;
        try {
          const issueItems = normalizeList(await getIssueItems(issueId));
          const total = issueItems.reduce(
            (sum, it) => sum + (Number(it.issued_quantity ?? it.requested_quantity) || 0), 0,
          );
          return [issueId, total];
        } catch (e) {
          console.error('[RequisitionDetail] load issue items failed:', e);
          return null;
        }
      }));
      setIssuedQtyByIssue(Object.fromEntries(qtyEntries.filter(Boolean)));
      const defaults = {};
      list.forEach((item) => {
        const lineId = resolveLineId(item);
        if (!lineId) return;
        const requested = Number(item.requested_quantity) || 0;
        const approved = Number(item.approved_quantity);
        defaults[lineId] = approved > 0 ? String(approved) : String(requested);
      });
      setApprovedQtys(defaults);
    } catch (e) { setError(formatDispensingError(e, 'Failed to load requisition')); }
    finally { setLoading(false); }
  }

  function resolveStoreName(storeId) {
    if (!storeId) return null;
    const store = stores.find(s => (s.store_id || s.id) === storeId);
    return storeLabel(store) || storeId;
  }

  useEffect(() => { load(); }, [id]);

  async function executeAction() {
    const required = DIALOG_ACTION_PERMISSION[dialog.action];
    if (required && !can(required)) return;
    setActioning(true);
    setActionError(null);
    try {
      const scope = resolveDispensingScope(req, stores);
      if (dialog.action === 'submit') await submitRequisition(id, scope);
      else if (dialog.action === 'approve') {
        const built = buildApproveItems(reqItems, approvedQtys);
        if (built.error) {
          setActionError(built.error);
          setActioning(false);
          return;
        }
        await approveRequisition(id, {
          ...scope,
          ...(built.items ? { items: built.items } : {}),
        });
      } else if (dialog.action === 'reject') await rejectRequisition(id, { ...scope, reason });
      else if (dialog.action === 'release') await releaseRequisitionReservation(id, scope);
      else if (dialog.action === 'cancel') {
        await deleteRequisition(id, scope);
        history.push(`${stockBase}/requisitions`);
        return;
      }
      setDialog(null); setReason(''); load();
    } catch (e) {
      console.error('[RequisitionDetail] action failed:', e);
      setActionError(formatDispensingError(e, 'Action failed'));
    } finally { setActioning(false); }
  }

  function openApproveDialog() {
    const built = buildApproveItems(reqItems, approvedQtys);
    if (built.error) {
      setActionError(built.error);
      return;
    }
    const isPartial = Boolean(built.items);
    setDialog({
      action: 'approve',
      title: 'Approve Requisition',
      desc: isPartial
        ? 'Approve with the entered quantities? Stock will reserve against approved qty.'
        : 'Approve this requisition at full requested quantities?',
    });
  }

  function setApprovedQty(lineId, value) {
    setApprovedQtys(prev => ({ ...prev, [lineId]: value }));
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress sx={{ color: IPD_COLORS.primary }} /></Box>;
  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" action={
          <InvButton size="small" variant="outlined" tone="teal" onClick={() => load()}>Retry</InvButton>
        }>
          {error}
        </Alert>
      </Box>
    );
  }
  if (!req) return null;

  const { status } = req;
  const latestIssue = getLatestIssue(issues);
  const latestIssueStatus = resolveIssueStatus(latestIssue);
  const canIssueElsewhere = REQUISITION_ISSUABLE_STATUSES.includes(status) && req.is_fully_issued !== true;
  const actionButtons = [];
  if (status === 'DRAFT') {
    if (can(INV_ACTIONS.REQUISITION_EDIT)) actionButtons.push(
      <InvButton key="edit" size="small" variant="outlined" tone="teal"
        onClick={() => history.push(`${stockBase}/requisition/${id}/edit`)}>Edit</InvButton>
    );
    if (can(INV_ACTIONS.REQUISITION_SUBMIT)) actionButtons.push(
      <InvButton key="submit" size="small" tone="mint"
        onClick={() => setDialog({ action: 'submit', title: 'Submit Requisition', desc: 'Submit for approval?' })}>Submit</InvButton>
    );
    if (can(INV_ACTIONS.REQUISITION_CANCEL)) actionButtons.push(
      <InvButton key="cancel" size="small" tone="red" variant="outlined"
        onClick={() => setDialog({ action: 'cancel', title: 'Cancel Requisition', desc: 'Cancel this draft requisition? It will be marked CANCELLED.', destructive: true })}>Cancel</InvButton>
    );
  }
  if (status === 'SUBMITTED') {
    if (can(INV_ACTIONS.REQUISITION_APPROVE)) actionButtons.push(
      <InvButton key="approve" size="small"
        onClick={openApproveDialog}>Approve</InvButton>
    );
    if (can(INV_ACTIONS.REQUISITION_REJECT)) actionButtons.push(
      <InvButton key="reject" size="small" tone="red" variant="outlined"
        onClick={() => setDialog({ action: 'reject', title: 'Reject Requisition', desc: 'Reject this requisition?', destructive: true, requireReason: true })}>Reject</InvButton>
    );
  }
  if (status === 'RESERVED' && can(INV_ACTIONS.REQUISITION_RELEASE)) actionButtons.push(
    <InvButton key="release" size="small" variant="outlined" tone="teal"
      onClick={() => setDialog({ action: 'release', title: 'Release Reservation', desc: 'Release the reserved stock? The requisition returns to APPROVED.' })}>Release Reservation</InvButton>
  );

  return (
    <Box>
      <PageHeader eyebrow="Inventory Management › Requisitions" title={req.transaction_number}
        actions={
          <>
            <InvButton variant="outlined" tone="teal" size="small" onClick={() => history.goBack()}>Back</InvButton>
            {actionButtons}
          </>
        }
      />
      {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>{actionError}</Alert>}
      {canIssueElsewhere && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <InvButton size="small" tone="mint"
              onClick={() => history.push(stockBase)}>
              Go to Stock Dashboard
            </InvButton>
          }
        >
          To issue stock, use Stock Dashboard → Store Queues → Pending Requisitions (opens the issue form directly).
        </Alert>
      )}
      {status === 'APPROVED' && !req.reservation_reference_id && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Approved without stock reservation — issues will deduct directly from available central store stock.
        </Alert>
      )}
      <RequisitionTimeline reqStatus={status} latestIssueStatus={latestIssueStatus} />
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={5}>
          <SectionCard title="Details" actions={
            <Box sx={{ display: 'flex', gap: 2 }}>
              <BadgeStack label="Requisition Status">
                <StatusChip status={req.status} domain="requisition" />
              </BadgeStack>
              {latestIssue && (
                <BadgeStack label="Issue Status">
                  <StatusChip status={latestIssueStatus} domain="issue" />
                </BadgeStack>
              )}
            </Box>
          }>
            <DetailRow label="From Store (Requester)">{resolveStoreName(req.from_store_id)}</DetailRow>
            <DetailRow label="To Store (Main)">{resolveStoreName(req.to_store_id)}</DetailRow>
            <DetailRow label="Priority">{req.priority}</DetailRow>
            <DetailRow label="Date">{formatDate(req.transaction_date)}</DetailRow>
            <DetailRow label="Remarks">{req.remarks}</DetailRow>
            {req.reservation_reference_id && (
              <DetailRow label="Reservation">{req.reservation_reference_id}</DetailRow>
            )}
            {req.is_fully_issued && (
              <DetailRow label="Fully Issued">Yes</DetailRow>
            )}
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={7}>
          <SectionCard title="Items Requested">
            <Alert severity="info" sx={{ mb: 1.5 }}>
              {status === 'SUBMITTED' && can(INV_ACTIONS.REQUISITION_APPROVE)
                ? 'Set Approved Qty per line (defaults to Requested). You may approve less than requested.'
                : 'Requisition status and issued quantities update once the recipient acknowledges receipt.'}
            </Alert>
            <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Item</TableCell>
                  <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Requested</TableCell>
                  <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Approved Qty</TableCell>
                  <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>
                    <Tooltip title="Updates after the recipient acknowledges receipt">
                      <span>Issued</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Remaining</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reqItems.length === 0
                  ? <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', color: IPD_COLORS.textSecondary }}>No items</TableCell></TableRow>
                  : reqItems.map((item, i) => {
                    const lineId = resolveLineId(item);
                    const requested = Number(item.requested_quantity) || 0;
                    const approved = Number(item.approved_quantity);
                    const approveCap = approved > 0 ? approved : requested;
                    const issued = Number(item.issued_quantity) || 0;
                    const remaining = Math.max(0, approveCap - issued);
                    const canEditApproved = status === 'SUBMITTED' && lineId
                      && can(INV_ACTIONS.REQUISITION_APPROVE);
                    return (
                      <TableRow key={lineId || i}>
                        <TableCell>{resolveItem(item)}</TableCell>
                        <TableCell align="right">{formatQty(item.requested_quantity)}</TableCell>
                        <TableCell align="right" sx={{ minWidth: canEditApproved ? 120 : undefined }}>
                          {canEditApproved ? (
                            <InvTextField
                              type="number"
                              label=""
                              value={approvedQtys[lineId] ?? ''}
                              onChange={e => setApprovedQty(lineId, e.target.value)}
                              inputProps={{ min: 0, max: requested, step: 'any' }}
                              sx={{ maxWidth: 120, ml: 'auto' }}
                            />
                          ) : (
                            formatQty(item.approved_quantity)
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ color: IPD_COLORS.success.text }}>{item.issued_quantity != null ? formatQty(item.issued_quantity) : '—'}</TableCell>
                        <TableCell align="right">{formatQty(remaining)}</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </SectionCard>

          <SectionCard title="Issues" sx={{ mt: 2.5 }}>
            <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>#</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Issue #</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Status</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Date</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Acknowledged At</TableCell>
                  <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Issued Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {issues.length === 0
                  ? <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', color: IPD_COLORS.textSecondary }}>No issues raised yet</TableCell></TableRow>
                  : issues.map((issue, i) => {
                    const issueId = resolveIssueId(issue);
                    const issueStatus = resolveIssueStatus(issue);
                    return (
                      <TableRow key={issueId ?? i} hover>
                        <TableCell>{issue.issue_sequence ?? i + 1}</TableCell>
                        <TableCell sx={{ color: IPD_COLORS.primary, cursor: 'pointer' }}
                          onClick={() => history.push(`${stockBase}/issues/${issueId}`)}>
                          {issue.transaction_number}
                        </TableCell>
                        <TableCell><StatusChip status={issueStatus} domain="issue" /></TableCell>
                        <TableCell>{formatDate(issue.transaction_date)}</TableCell>
                        <TableCell>
                          {issue.acknowledged_at
                            ? formatDate(issue.acknowledged_at)
                            : <Typography component="span" sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>Awaiting Acknowledgement</Typography>}
                        </TableCell>
                        <TableCell align="right">{formatQty(issuedQtyByIssue[issueId])}</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </SectionCard>
        </Grid>
      </Grid>
      <ConfirmDialog open={Boolean(dialog)} title={dialog?.title} description={dialog?.desc}
        destructive={dialog?.destructive} requireReason={dialog?.requireReason}
        reasonValue={reason} onReasonChange={setReason} loading={actioning}
        onConfirm={executeAction} onCancel={() => { setDialog(null); setReason(''); }} />
    </Box>
  );
}
